import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FileText, Download, Eye, CheckCircle, ArrowsClockwise, EnvelopeSimple,
  Books, WarningCircle, Sparkle, ListChecks,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATO_COLORS = {
  conforme: 'bg-green-100 text-green-700',
  da_adeguare: 'bg-red-100 text-red-700',
  da_approfondire: 'bg-yellow-100 text-yellow-700',
};
const STATO_LABEL = {
  conforme: 'Conforme',
  da_adeguare: 'Da adeguare',
  da_approfondire: 'Da approfondire',
};

export default function ComplianceReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [details, setDetails] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/compliance/reports?limit=50`, { credentials: 'include' });
      if (res.ok) setReports((await res.json()).reports || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetails = async (r) => {
    setActive(r);
    setDetails(null);
    try {
      const res = await fetch(`${API}/api/compliance/reports/${r.id}`, { credentials: 'include' });
      if (res.ok) setDetails(await res.json());
      // mark read
      fetch(`${API}/api/compliance/reports/${r.id}/read`, { method: 'POST', credentials: 'include' });
    } catch { toast.error('Errore caricamento dettagli'); }
  };

  const downloadPdf = (rid, filename) => {
    const url = `${API}/api/compliance/reports/${rid}/download`;
    fetch(url, { credentials: 'include' })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename || 'relazione.pdf';
        link.click();
      });
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento relazioni...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="compliance-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileText size={32} weight="duotone" className="text-orange-500" />
            Relazioni Delibere ARERA
          </h1>
          <p className="text-slate-500 mt-1">
            Relazioni AI generate automaticamente per le delibere high-impact. Ogni delibera è anche indicizzata nella Knowledge Base.
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="reload-reports-btn">
          <ArrowsClockwise size={16} className="mr-2" /> Aggiorna
        </Button>
      </div>

      {reports.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <FileText size={64} weight="duotone" className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nessuna relazione ancora.</p>
          <p className="text-xs text-slate-400 mt-2">
            Le relazioni vengono generate automaticamente dal Compliance Watcher (Lun 06:00) per le delibere high-impact.
            Puoi anche generare manualmente da "Raccolte dati e richieste" su qualunque alert.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {reports.map(r => {
            const ex = r.analysis?.executive || {};
            return (
              <div key={r.id}
                   className={`bg-white rounded-lg border p-4 hover:shadow-md transition cursor-pointer ${
                     !r.is_read ? 'border-l-4 border-l-orange-500' : ''}`}
                   onClick={() => openDetails(r)}
                   data-testid={`report-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className="font-mono text-xs">{r.delibera_id || '—'}</Badge>
                      {ex.ambito && <Badge variant="outline" className="uppercase text-[10px]">{ex.ambito}</Badge>}
                      <Badge className={STATO_COLORS[r.stato] || 'bg-slate-100 text-slate-600'}>
                        {STATO_LABEL[r.stato] || r.stato}
                      </Badge>
                      {!r.is_read && <Badge className="bg-orange-100 text-orange-700">Nuova</Badge>}
                      {r.kb_ingested && (
                        <Badge variant="outline" className="text-blue-700 border-blue-300">
                          <Books size={10} className="mr-1" /> KB ({r.kb_chunks})
                        </Badge>
                      )}
                      {r.email_status === 'sent' && (
                        <Badge variant="outline" className="text-green-700 border-green-300">
                          <EnvelopeSimple size={10} className="mr-1" /> Email inviata
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-slate-900 truncate">{r.title}</h3>
                    {ex.sintesi && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{ex.sintesi}</p>
                    )}
                    <div className="text-xs text-slate-400 mt-2">
                      Generata: {new Date(r.created_at).toLocaleString('it-IT')}
                      {r.email_sent_to?.length > 0 && ` · Inviata a ${r.email_sent_to.length} dest.`}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDetails(r); }}>
                      <Eye size={14} className="mr-1" /> Vedi
                    </Button>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); downloadPdf(r.id, r.pdf_filename); }}
                            data-testid={`download-${r.id}`}>
                      <Download size={14} className="mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DETAILS MODAL */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDetails(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={20} weight="duotone" className="text-orange-500" />
              {active?.delibera_id || 'Relazione'}
            </DialogTitle>
          </DialogHeader>
          {!details ? (
            <div className="text-center py-8 text-slate-500">Caricamento...</div>
          ) : (
            <ReportDetails r={details} onDownload={() => downloadPdf(active.id, active.pdf_filename)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportDetails({ r, onDownload }) {
  const a = r.analysis || {};
  const ex = a.executive || {};
  return (
    <div className="space-y-4">
      <div className="bg-orange-50 border-l-4 border-orange-500 rounded p-3">
        <h3 className="font-semibold text-slate-900">{r.title}</h3>
        <p className="text-sm text-slate-700 mt-2">{ex.sintesi || 'Sintesi non disponibile'}</p>
        <div className="grid grid-cols-2 gap-2 text-xs mt-3 text-slate-600">
          <div><strong>Ambito:</strong> {ex.ambito || '—'}</div>
          <div><strong>Decorrenza:</strong> {ex.decorrenza || '—'}</div>
          <div><strong>Pubblicazione:</strong> {ex.data_pubblicazione || '—'}</div>
          <div><strong>Scadenza:</strong> {ex.scadenza_adeguamento || 'N/A'}</div>
          <div className="col-span-2"><strong>Stato:</strong> <Badge className={STATO_COLORS[r.stato]}>{STATO_LABEL[r.stato]}</Badge></div>
        </div>
      </div>

      {(a.top_cambiamenti || []).length > 0 && (
        <div>
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
            <WarningCircle size={14} className="text-orange-500" /> Top cambiamenti
          </h4>
          <div className="space-y-2">
            {a.top_cambiamenti.map((c, i) => (
              <div key={i} className="border-l-2 pl-3 py-1"
                   style={{ borderColor: c.criticita === 'alta' ? '#dc2626' : c.criticita === 'media' ? '#f59e0b' : '#16a34a' }}>
                <div className="font-medium text-sm">{c.titolo}</div>
                <div className="text-xs text-slate-600">{c.descrizione}</div>
                <Badge className="mt-1 text-[10px]" variant="outline">Crit: {c.criticita}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {(a.checklist_operativa || []).length > 0 && (
        <div>
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
            <ListChecks size={14} className="text-orange-500" /> Checklist operativa
          </h4>
          <ul className="space-y-1 text-sm">
            {a.checklist_operativa.map((item, i) => (
              <li key={i} className="flex gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 shrink-0" /> {item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 pt-3 border-t">
        <Button onClick={onDownload} className="bg-orange-500 hover:bg-orange-600">
          <Download size={14} className="mr-1" /> Scarica relazione PDF
        </Button>
        {r.kb_ingested && (
          <Badge variant="outline" className="text-blue-700 px-3 py-2">
            <Books size={12} className="mr-1" /> Indicizzata in Knowledge Base ({r.kb_chunks} chunks)
          </Badge>
        )}
      </div>
    </div>
  );
}
